import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper";
import { Environments } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { DevCenterClient } from "../devCenterClient";
import {
  Catalog,
  EnvironmentsListCatalogsByProjectNextOptionalParams,
  EnvironmentsListCatalogsByProjectOptionalParams,
  EnvironmentsListCatalogsByProjectResponse,
  EnvironmentDefinition,
  EnvironmentsListEnvironmentDefinitionsByCatalogNextOptionalParams,
  EnvironmentsListEnvironmentDefinitionsByCatalogOptionalParams,
  EnvironmentsListEnvironmentDefinitionsByCatalogResponse,
  EnvironmentsListEnvironmentDefinitionsByProjectNextOptionalParams,
  EnvironmentsListEnvironmentDefinitionsByProjectOptionalParams,
  EnvironmentsListEnvironmentDefinitionsByProjectResponse,
  EnvironmentType,
  EnvironmentsListEnvironmentTypesNextOptionalParams,
  EnvironmentsListEnvironmentTypesOptionalParams,
  EnvironmentsListEnvironmentTypesResponse,
  Environment,
  EnvironmentsListEnvironmentsNextOptionalParams,
  EnvironmentsListEnvironmentsOptionalParams,
  EnvironmentsListEnvironmentsResponse,
  EnvironmentsListEnvironmentsByUserNextOptionalParams,
  EnvironmentsListEnvironmentsByUserOptionalParams,
  EnvironmentsListEnvironmentsByUserResponse,
  EnvironmentAction,
  EnvironmentsListActionsNextOptionalParams,
  EnvironmentsListActionsOptionalParams,
  EnvironmentsListActionsResponse,
  EnvironmentOperationUnion,
  EnvironmentsListOperationsNextOptionalParams,
  EnvironmentsListOperationsOptionalParams,
  EnvironmentsListOperationsResponse,
  EnvironmentsGetCatalogOptionalParams,
  EnvironmentsGetCatalogResponse,
  EnvironmentsGetEnvironmentDefinitionOptionalParams,
  EnvironmentsGetEnvironmentDefinitionResponse,
  EnvironmentsGetEnvironmentTypesOptionalParams,
  EnvironmentsGetEnvironmentTypesResponse,
  EnvironmentsGetEnvironmentTypeAbilitiesOptionalParams,
  EnvironmentsGetEnvironmentTypeAbilitiesResponse,
  EnvironmentsGetEnvironmentByUserOptionalParams,
  EnvironmentsGetEnvironmentByUserResponse,
  EnvironmentsCreateOrReplaceEnvironmentOptionalParams,
  EnvironmentsCreateOrReplaceEnvironmentResponse,
  EnvironmentPatchProperties,
  EnvironmentsPatchEnvironmentOptionalParams,
  EnvironmentsPatchEnvironmentResponse,
  EnvironmentsDeleteEnvironmentOptionalParams,
  EnvironmentsDeleteEnvironmentResponse,
  EnvironmentsGetActionOptionalParams,
  EnvironmentsGetActionResponse,
  EnvironmentsSkipActionOptionalParams,
  EnvironmentsDelayActionOptionalParams,
  EnvironmentsDelayActionResponse,
  EnvironmentsGetOperationOptionalParams,
  EnvironmentsGetOperationResponse,
  EnvironmentsGetLogsByOperationOptionalParams,
  EnvironmentsGetLogsByOperationResponse,
  EnvironmentsGetOutputsOptionalParams,
  EnvironmentsGetOutputsResponse,
  EnvironmentsListCatalogsByProjectNextResponse,
  EnvironmentsListEnvironmentDefinitionsByCatalogNextResponse,
  EnvironmentsListEnvironmentDefinitionsByProjectNextResponse,
  EnvironmentsListEnvironmentTypesNextResponse,
  EnvironmentsListEnvironmentsNextResponse,
  EnvironmentsListEnvironmentsByUserNextResponse,
  EnvironmentsListActionsNextResponse,
  EnvironmentsListOperationsNextResponse,
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing Environments operations. */
export class EnvironmentsImpl implements Environments {
  private readonly client: DevCenterClient;

  /**
   * Initialize a new instance of the class Environments class.
   * @param client Reference to the service client
   */
  constructor(client: DevCenterClient) {
    this.client = client;
  }

  /**
   * Lists all of the catalogs available for a project.
   * @param projectName Name of the project.
   * @param options The options parameters.
   */
  public listCatalogsByProject(
    projectName: string,
    options?: EnvironmentsListCatalogsByProjectOptionalParams,
  ): PagedAsyncIterableIterator<Catalog> {
    const iter = this.listCatalogsByProjectPagingAll(projectName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listCatalogsByProjectPagingPage(
          projectName,
          options,
          settings,
        );
      },
    };
  }

  private async *listCatalogsByProjectPagingPage(
    projectName: string,
    options?: EnvironmentsListCatalogsByProjectOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<Catalog[]> {
    let result: EnvironmentsListCatalogsByProjectResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listCatalogsByProject(projectName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listCatalogsByProjectNext(
        projectName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listCatalogsByProjectPagingAll(
    projectName: string,
    options?: EnvironmentsListCatalogsByProjectOptionalParams,
  ): AsyncIterableIterator<Catalog> {
    for await (const page of this.listCatalogsByProjectPagingPage(
      projectName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all environment definitions available within a catalog.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param catalogName The name of the catalog
   * @param options The options parameters.
   */
  public listEnvironmentDefinitionsByCatalog(
    projectName: string,
    catalogName: string,
    options?: EnvironmentsListEnvironmentDefinitionsByCatalogOptionalParams,
  ): PagedAsyncIterableIterator<EnvironmentDefinition> {
    const iter = this.listEnvironmentDefinitionsByCatalogPagingAll(
      projectName,
      catalogName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listEnvironmentDefinitionsByCatalogPagingPage(
          projectName,
          catalogName,
          options,
          settings,
        );
      },
    };
  }

  private async *listEnvironmentDefinitionsByCatalogPagingPage(
    projectName: string,
    catalogName: string,
    options?: EnvironmentsListEnvironmentDefinitionsByCatalogOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<EnvironmentDefinition[]> {
    let result: EnvironmentsListEnvironmentDefinitionsByCatalogResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listEnvironmentDefinitionsByCatalog(
        projectName,
        catalogName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listEnvironmentDefinitionsByCatalogNext(
        projectName,
        catalogName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listEnvironmentDefinitionsByCatalogPagingAll(
    projectName: string,
    catalogName: string,
    options?: EnvironmentsListEnvironmentDefinitionsByCatalogOptionalParams,
  ): AsyncIterableIterator<EnvironmentDefinition> {
    for await (const page of this.listEnvironmentDefinitionsByCatalogPagingPage(
      projectName,
      catalogName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all environment definitions available for a project.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  public listEnvironmentDefinitionsByProject(
    projectName: string,
    options?: EnvironmentsListEnvironmentDefinitionsByProjectOptionalParams,
  ): PagedAsyncIterableIterator<EnvironmentDefinition> {
    const iter = this.listEnvironmentDefinitionsByProjectPagingAll(
      projectName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listEnvironmentDefinitionsByProjectPagingPage(
          projectName,
          options,
          settings,
        );
      },
    };
  }

  private async *listEnvironmentDefinitionsByProjectPagingPage(
    projectName: string,
    options?: EnvironmentsListEnvironmentDefinitionsByProjectOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<EnvironmentDefinition[]> {
    let result: EnvironmentsListEnvironmentDefinitionsByProjectResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listEnvironmentDefinitionsByProject(
        projectName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listEnvironmentDefinitionsByProjectNext(
        projectName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listEnvironmentDefinitionsByProjectPagingAll(
    projectName: string,
    options?: EnvironmentsListEnvironmentDefinitionsByProjectOptionalParams,
  ): AsyncIterableIterator<EnvironmentDefinition> {
    for await (const page of this.listEnvironmentDefinitionsByProjectPagingPage(
      projectName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all environment types configured for a project.
   * @param projectName Name of the project.
   * @param options The options parameters.
   */
  public listEnvironmentTypes(
    projectName: string,
    options?: EnvironmentsListEnvironmentTypesOptionalParams,
  ): PagedAsyncIterableIterator<EnvironmentType> {
    const iter = this.listEnvironmentTypesPagingAll(projectName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listEnvironmentTypesPagingPage(
          projectName,
          options,
          settings,
        );
      },
    };
  }

  private async *listEnvironmentTypesPagingPage(
    projectName: string,
    options?: EnvironmentsListEnvironmentTypesOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<EnvironmentType[]> {
    let result: EnvironmentsListEnvironmentTypesResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listEnvironmentTypes(projectName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listEnvironmentTypesNext(
        projectName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listEnvironmentTypesPagingAll(
    projectName: string,
    options?: EnvironmentsListEnvironmentTypesOptionalParams,
  ): AsyncIterableIterator<EnvironmentType> {
    for await (const page of this.listEnvironmentTypesPagingPage(
      projectName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists the environments for a project.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  public listEnvironments(
    projectName: string,
    options?: EnvironmentsListEnvironmentsOptionalParams,
  ): PagedAsyncIterableIterator<Environment> {
    const iter = this.listEnvironmentsPagingAll(projectName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listEnvironmentsPagingPage(projectName, options, settings);
      },
    };
  }

  private async *listEnvironmentsPagingPage(
    projectName: string,
    options?: EnvironmentsListEnvironmentsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<Environment[]> {
    let result: EnvironmentsListEnvironmentsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listEnvironments(projectName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listEnvironmentsNext(
        projectName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listEnvironmentsPagingAll(
    projectName: string,
    options?: EnvironmentsListEnvironmentsOptionalParams,
  ): AsyncIterableIterator<Environment> {
    for await (const page of this.listEnvironmentsPagingPage(
      projectName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists the environments for a project and user.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param options The options parameters.
   */
  public listEnvironmentsByUser(
    projectName: string,
    userId: string,
    options?: EnvironmentsListEnvironmentsByUserOptionalParams,
  ): PagedAsyncIterableIterator<Environment> {
    const iter = this.listEnvironmentsByUserPagingAll(
      projectName,
      userId,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listEnvironmentsByUserPagingPage(
          projectName,
          userId,
          options,
          settings,
        );
      },
    };
  }

  private async *listEnvironmentsByUserPagingPage(
    projectName: string,
    userId: string,
    options?: EnvironmentsListEnvironmentsByUserOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<Environment[]> {
    let result: EnvironmentsListEnvironmentsByUserResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listEnvironmentsByUser(projectName, userId, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listEnvironmentsByUserNext(
        projectName,
        userId,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listEnvironmentsByUserPagingAll(
    projectName: string,
    userId: string,
    options?: EnvironmentsListEnvironmentsByUserOptionalParams,
  ): AsyncIterableIterator<Environment> {
    for await (const page of this.listEnvironmentsByUserPagingPage(
      projectName,
      userId,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Get all scheduled actions for a user within an environment.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param options The options parameters.
   */
  public listActions(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsListActionsOptionalParams,
  ): PagedAsyncIterableIterator<EnvironmentAction> {
    const iter = this.listActionsPagingAll(
      projectName,
      userId,
      environmentName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listActionsPagingPage(
          projectName,
          userId,
          environmentName,
          options,
          settings,
        );
      },
    };
  }

  private async *listActionsPagingPage(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsListActionsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<EnvironmentAction[]> {
    let result: EnvironmentsListActionsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listActions(
        projectName,
        userId,
        environmentName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listActionsNext(
        projectName,
        userId,
        environmentName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listActionsPagingAll(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsListActionsOptionalParams,
  ): AsyncIterableIterator<EnvironmentAction> {
    for await (const page of this.listActionsPagingPage(
      projectName,
      userId,
      environmentName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists operations on the environment which have occurred within the past 90 days
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param options The options parameters.
   */
  public listOperations(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsListOperationsOptionalParams,
  ): PagedAsyncIterableIterator<EnvironmentOperationUnion> {
    const iter = this.listOperationsPagingAll(
      projectName,
      userId,
      environmentName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listOperationsPagingPage(
          projectName,
          userId,
          environmentName,
          options,
          settings,
        );
      },
    };
  }

  private async *listOperationsPagingPage(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsListOperationsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<EnvironmentOperationUnion[]> {
    let result: EnvironmentsListOperationsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listOperations(
        projectName,
        userId,
        environmentName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listOperationsNext(
        projectName,
        userId,
        environmentName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listOperationsPagingAll(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsListOperationsOptionalParams,
  ): AsyncIterableIterator<EnvironmentOperationUnion> {
    for await (const page of this.listOperationsPagingPage(
      projectName,
      userId,
      environmentName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all of the catalogs available for a project.
   * @param projectName Name of the project.
   * @param options The options parameters.
   */
  private _listCatalogsByProject(
    projectName: string,
    options?: EnvironmentsListCatalogsByProjectOptionalParams,
  ): Promise<EnvironmentsListCatalogsByProjectResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      listCatalogsByProjectOperationSpec,
    );
  }

  /**
   * Gets the specified catalog within the project.
   * @param projectName Name of the project.
   * @param catalogName Name of the catalog.
   * @param options The options parameters.
   */
  getCatalog(
    projectName: string,
    catalogName: string,
    options?: EnvironmentsGetCatalogOptionalParams,
  ): Promise<EnvironmentsGetCatalogResponse> {
    return this.client.sendOperationRequest(
      { projectName, catalogName, options },
      getCatalogOperationSpec,
    );
  }

  /**
   * Lists all environment definitions available within a catalog.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param catalogName The name of the catalog
   * @param options The options parameters.
   */
  private _listEnvironmentDefinitionsByCatalog(
    projectName: string,
    catalogName: string,
    options?: EnvironmentsListEnvironmentDefinitionsByCatalogOptionalParams,
  ): Promise<EnvironmentsListEnvironmentDefinitionsByCatalogResponse> {
    return this.client.sendOperationRequest(
      { projectName, catalogName, options },
      listEnvironmentDefinitionsByCatalogOperationSpec,
    );
  }

  /**
   * Get an environment definition from a catalog.
   * @param projectName Name of the project.
   * @param catalogName Name of the catalog.
   * @param definitionName Name of the environment definition.
   * @param options The options parameters.
   */
  getEnvironmentDefinition(
    projectName: string,
    catalogName: string,
    definitionName: string,
    options?: EnvironmentsGetEnvironmentDefinitionOptionalParams,
  ): Promise<EnvironmentsGetEnvironmentDefinitionResponse> {
    return this.client.sendOperationRequest(
      { projectName, catalogName, definitionName, options },
      getEnvironmentDefinitionOperationSpec,
    );
  }

  /**
   * Lists all environment definitions available for a project.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  private _listEnvironmentDefinitionsByProject(
    projectName: string,
    options?: EnvironmentsListEnvironmentDefinitionsByProjectOptionalParams,
  ): Promise<EnvironmentsListEnvironmentDefinitionsByProjectResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      listEnvironmentDefinitionsByProjectOperationSpec,
    );
  }

  /**
   * Lists all environment types configured for a project.
   * @param projectName Name of the project.
   * @param options The options parameters.
   */
  private _listEnvironmentTypes(
    projectName: string,
    options?: EnvironmentsListEnvironmentTypesOptionalParams,
  ): Promise<EnvironmentsListEnvironmentTypesResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      listEnvironmentTypesOperationSpec,
    );
  }

  /**
   * Get an environment type configured for a project.
   * @param projectName Name of the project.
   * @param environmentTypeName Name of the environment type.
   * @param options The options parameters.
   */
  getEnvironmentTypes(
    projectName: string,
    environmentTypeName: string,
    options?: EnvironmentsGetEnvironmentTypesOptionalParams,
  ): Promise<EnvironmentsGetEnvironmentTypesResponse> {
    return this.client.sendOperationRequest(
      { projectName, environmentTypeName, options },
      getEnvironmentTypesOperationSpec,
    );
  }

  /**
   * Gets the signed-in user's permitted abilities in an environment type.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param environmentTypeName The name of the environment type
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param options The options parameters.
   */
  getEnvironmentTypeAbilities(
    projectName: string,
    environmentTypeName: string,
    userId: string,
    options?: EnvironmentsGetEnvironmentTypeAbilitiesOptionalParams,
  ): Promise<EnvironmentsGetEnvironmentTypeAbilitiesResponse> {
    return this.client.sendOperationRequest(
      { projectName, environmentTypeName, userId, options },
      getEnvironmentTypeAbilitiesOperationSpec,
    );
  }

  /**
   * Lists the environments for a project.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  private _listEnvironments(
    projectName: string,
    options?: EnvironmentsListEnvironmentsOptionalParams,
  ): Promise<EnvironmentsListEnvironmentsResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      listEnvironmentsOperationSpec,
    );
  }

  /**
   * Lists the environments for a project and user.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param options The options parameters.
   */
  private _listEnvironmentsByUser(
    projectName: string,
    userId: string,
    options?: EnvironmentsListEnvironmentsByUserOptionalParams,
  ): Promise<EnvironmentsListEnvironmentsByUserResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, options },
      listEnvironmentsByUserOperationSpec,
    );
  }

  /**
   * Gets an environment.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param options The options parameters.
   */
  getEnvironmentByUser(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsGetEnvironmentByUserOptionalParams,
  ): Promise<EnvironmentsGetEnvironmentByUserResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, options },
      getEnvironmentByUserOperationSpec,
    );
  }

  /**
   * Creates or updates an environment.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName The name of the environment.
   * @param body Represents an environment.
   * @param options The options parameters.
   */
  createOrReplaceEnvironment(
    projectName: string,
    userId: string,
    environmentName: string,
    body: Environment,
    options?: EnvironmentsCreateOrReplaceEnvironmentOptionalParams,
  ): Promise<EnvironmentsCreateOrReplaceEnvironmentResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, body, options },
      createOrReplaceEnvironmentOperationSpec,
    );
  }

  /**
   * Partially updates an environment.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName The name of the environment.
   * @param body Represents an environment.
   * @param options The options parameters.
   */
  patchEnvironment(
    projectName: string,
    userId: string,
    environmentName: string,
    body: EnvironmentPatchProperties,
    options?: EnvironmentsPatchEnvironmentOptionalParams,
  ): Promise<EnvironmentsPatchEnvironmentResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, body, options },
      patchEnvironmentOperationSpec,
    );
  }

  /**
   * Deletes an environment and all its associated resources
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName The name of the environment.
   * @param options The options parameters.
   */
  deleteEnvironment(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsDeleteEnvironmentOptionalParams,
  ): Promise<EnvironmentsDeleteEnvironmentResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, options },
      deleteEnvironmentOperationSpec,
    );
  }

  /**
   * Get all scheduled actions for a user within an environment.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param options The options parameters.
   */
  private _listActions(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsListActionsOptionalParams,
  ): Promise<EnvironmentsListActionsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, options },
      listActionsOperationSpec,
    );
  }

  /**
   * Retrieve a specific environment action.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param actionName Uniquely identifies the action.
   * @param options The options parameters.
   */
  getAction(
    projectName: string,
    userId: string,
    environmentName: string,
    actionName: string,
    options?: EnvironmentsGetActionOptionalParams,
  ): Promise<EnvironmentsGetActionResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, actionName, options },
      getActionOperationSpec,
    );
  }

  /**
   * Skips an occurrence of an action.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param actionName Uniquely identifies the action.
   * @param options The options parameters.
   */
  skipAction(
    projectName: string,
    userId: string,
    environmentName: string,
    actionName: string,
    options?: EnvironmentsSkipActionOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, actionName, options },
      skipActionOperationSpec,
    );
  }

  /**
   * Delays the occurrence of an action.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param actionName Uniquely identifies the action.
   * @param until The time to delay the Environment action until, in RFC3339 format.
   * @param options The options parameters.
   */
  delayAction(
    projectName: string,
    userId: string,
    environmentName: string,
    actionName: string,
    until: Date,
    options?: EnvironmentsDelayActionOptionalParams,
  ): Promise<EnvironmentsDelayActionResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, actionName, until, options },
      delayActionOperationSpec,
    );
  }

  /**
   * Lists operations on the environment which have occurred within the past 90 days
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param options The options parameters.
   */
  private _listOperations(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsListOperationsOptionalParams,
  ): Promise<EnvironmentsListOperationsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, options },
      listOperationsOperationSpec,
    );
  }

  /**
   * Gets an environment action result.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param operationId Unique identifier for the environment operation.
   * @param options The options parameters.
   */
  getOperation(
    projectName: string,
    userId: string,
    environmentName: string,
    operationId: string,
    options?: EnvironmentsGetOperationOptionalParams,
  ): Promise<EnvironmentsGetOperationResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, operationId, options },
      getOperationOperationSpec,
    );
  }

  /**
   * Gets the logs for an operation on an environment.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName The name of the environment.
   * @param operationId The id of the operation on an environment.
   * @param options The options parameters.
   */
  getLogsByOperation(
    projectName: string,
    userId: string,
    environmentName: string,
    operationId: string,
    options?: EnvironmentsGetLogsByOperationOptionalParams,
  ): Promise<EnvironmentsGetLogsByOperationResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, operationId, options },
      getLogsByOperationOperationSpec,
    );
  }

  /**
   * Gets Outputs from the environment.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName The name of the environment.
   * @param options The options parameters.
   */
  getOutputs(
    projectName: string,
    userId: string,
    environmentName: string,
    options?: EnvironmentsGetOutputsOptionalParams,
  ): Promise<EnvironmentsGetOutputsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, options },
      getOutputsOperationSpec,
    );
  }

  /**
   * ListCatalogsByProjectNext
   * @param projectName Name of the project.
   * @param nextLink The nextLink from the previous successful call to the ListCatalogsByProject method.
   * @param options The options parameters.
   */
  private _listCatalogsByProjectNext(
    projectName: string,
    nextLink: string,
    options?: EnvironmentsListCatalogsByProjectNextOptionalParams,
  ): Promise<EnvironmentsListCatalogsByProjectNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, nextLink, options },
      listCatalogsByProjectNextOperationSpec,
    );
  }

  /**
   * ListEnvironmentDefinitionsByCatalogNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param catalogName The name of the catalog
   * @param nextLink The nextLink from the previous successful call to the
   *                 ListEnvironmentDefinitionsByCatalog method.
   * @param options The options parameters.
   */
  private _listEnvironmentDefinitionsByCatalogNext(
    projectName: string,
    catalogName: string,
    nextLink: string,
    options?: EnvironmentsListEnvironmentDefinitionsByCatalogNextOptionalParams,
  ): Promise<EnvironmentsListEnvironmentDefinitionsByCatalogNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, catalogName, nextLink, options },
      listEnvironmentDefinitionsByCatalogNextOperationSpec,
    );
  }

  /**
   * ListEnvironmentDefinitionsByProjectNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param nextLink The nextLink from the previous successful call to the
   *                 ListEnvironmentDefinitionsByProject method.
   * @param options The options parameters.
   */
  private _listEnvironmentDefinitionsByProjectNext(
    projectName: string,
    nextLink: string,
    options?: EnvironmentsListEnvironmentDefinitionsByProjectNextOptionalParams,
  ): Promise<EnvironmentsListEnvironmentDefinitionsByProjectNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, nextLink, options },
      listEnvironmentDefinitionsByProjectNextOperationSpec,
    );
  }

  /**
   * ListEnvironmentTypesNext
   * @param projectName Name of the project.
   * @param nextLink The nextLink from the previous successful call to the ListEnvironmentTypes method.
   * @param options The options parameters.
   */
  private _listEnvironmentTypesNext(
    projectName: string,
    nextLink: string,
    options?: EnvironmentsListEnvironmentTypesNextOptionalParams,
  ): Promise<EnvironmentsListEnvironmentTypesNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, nextLink, options },
      listEnvironmentTypesNextOperationSpec,
    );
  }

  /**
   * ListEnvironmentsNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param nextLink The nextLink from the previous successful call to the ListEnvironments method.
   * @param options The options parameters.
   */
  private _listEnvironmentsNext(
    projectName: string,
    nextLink: string,
    options?: EnvironmentsListEnvironmentsNextOptionalParams,
  ): Promise<EnvironmentsListEnvironmentsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, nextLink, options },
      listEnvironmentsNextOperationSpec,
    );
  }

  /**
   * ListEnvironmentsByUserNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param nextLink The nextLink from the previous successful call to the ListEnvironmentsByUser method.
   * @param options The options parameters.
   */
  private _listEnvironmentsByUserNext(
    projectName: string,
    userId: string,
    nextLink: string,
    options?: EnvironmentsListEnvironmentsByUserNextOptionalParams,
  ): Promise<EnvironmentsListEnvironmentsByUserNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, nextLink, options },
      listEnvironmentsByUserNextOperationSpec,
    );
  }

  /**
   * ListActionsNext
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param nextLink The nextLink from the previous successful call to the ListActions method.
   * @param options The options parameters.
   */
  private _listActionsNext(
    projectName: string,
    userId: string,
    environmentName: string,
    nextLink: string,
    options?: EnvironmentsListActionsNextOptionalParams,
  ): Promise<EnvironmentsListActionsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, nextLink, options },
      listActionsNextOperationSpec,
    );
  }

  /**
   * ListOperationsNext
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param environmentName Environment name.
   * @param nextLink The nextLink from the previous successful call to the ListOperations method.
   * @param options The options parameters.
   */
  private _listOperationsNext(
    projectName: string,
    userId: string,
    environmentName: string,
    nextLink: string,
    options?: EnvironmentsListOperationsNextOptionalParams,
  ): Promise<EnvironmentsListOperationsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, environmentName, nextLink, options },
      listOperationsNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listCatalogsByProjectOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/catalogs",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedCatalog,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsListCatalogsByProjectExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.projectName],
  headerParameters: [Parameters.accept],
  serializer,
};
const getCatalogOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/catalogs/{catalogName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Catalog,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsGetCatalogExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.catalogName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listEnvironmentDefinitionsByCatalogOperationSpec: coreClient.OperationSpec =
  {
    path: "/projects/{projectName}/catalogs/{catalogName}/environmentDefinitions",
    httpMethod: "GET",
    responses: {
      200: {
        bodyMapper: Mappers.PagedEnvironmentDefinition,
      },
      default: {
        bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
        headersMapper:
          Mappers.EnvironmentsListEnvironmentDefinitionsByCatalogExceptionHeaders,
      },
    },
    queryParameters: [Parameters.apiVersion],
    urlParameters: [
      Parameters.endpoint,
      Parameters.projectName,
      Parameters.catalogName,
    ],
    headerParameters: [Parameters.accept],
    serializer,
  };
const getEnvironmentDefinitionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/catalogs/{catalogName}/environmentDefinitions/{definitionName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.EnvironmentDefinition,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper:
        Mappers.EnvironmentsGetEnvironmentDefinitionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.catalogName,
    Parameters.definitionName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listEnvironmentDefinitionsByProjectOperationSpec: coreClient.OperationSpec =
  {
    path: "/projects/{projectName}/environmentDefinitions",
    httpMethod: "GET",
    responses: {
      200: {
        bodyMapper: Mappers.PagedEnvironmentDefinition,
      },
      default: {
        bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
        headersMapper:
          Mappers.EnvironmentsListEnvironmentDefinitionsByProjectExceptionHeaders,
      },
    },
    queryParameters: [Parameters.apiVersion],
    urlParameters: [Parameters.endpoint, Parameters.projectName],
    headerParameters: [Parameters.accept],
    serializer,
  };
const listEnvironmentTypesOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/environmentTypes",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedEnvironmentType,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsListEnvironmentTypesExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.projectName],
  headerParameters: [Parameters.accept],
  serializer,
};
const getEnvironmentTypesOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/environmentTypes/{environmentTypeName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.EnvironmentType,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsGetEnvironmentTypesExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.environmentTypeName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getEnvironmentTypeAbilitiesOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/environmentTypes/{environmentTypeName}/users/{userId}/abilities",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.EnvironmentTypeAbilities,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper:
        Mappers.EnvironmentsGetEnvironmentTypeAbilitiesExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.top],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.environmentTypeName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listEnvironmentsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/environments",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedEnvironment,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsListEnvironmentsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.projectName],
  headerParameters: [Parameters.accept],
  serializer,
};
const listEnvironmentsByUserOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedEnvironment,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsListEnvironmentsByUserExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getEnvironmentByUserOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Environment,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsGetEnvironmentByUserExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOrReplaceEnvironmentOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}",
  httpMethod: "PUT",
  responses: {
    201: {
      bodyMapper: Mappers.Environment,
      headersMapper: Mappers.EnvironmentsCreateOrReplaceEnvironmentHeaders,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper:
        Mappers.EnvironmentsCreateOrReplaceEnvironmentExceptionHeaders,
    },
  },
  requestBody: Parameters.body3,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const patchEnvironmentOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}",
  httpMethod: "PATCH",
  responses: {
    200: {
      bodyMapper: Mappers.Environment,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsPatchEnvironmentExceptionHeaders,
    },
  },
  requestBody: Parameters.body4,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType1],
  mediaType: "json",
  serializer,
};
const deleteEnvironmentOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}",
  httpMethod: "DELETE",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.EnvironmentsDeleteEnvironmentHeaders,
    },
    204: {},
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsDeleteEnvironmentExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.force],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listActionsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}/actions",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedEnvironmentAction,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsListActionsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getActionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}/actions/{actionName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.EnvironmentAction,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsGetActionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.actionName,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const skipActionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}/actions/{actionName}:skip",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsSkipActionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.actionName,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const delayActionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}/actions/{actionName}:delay",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.EnvironmentAction,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsDelayActionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.until],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.actionName,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listOperationsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}/operations",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedEnvironmentOperation,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsListOperationsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}/operations/{operationId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.EnvironmentOperation,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsGetOperationExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.operationId,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getLogsByOperationOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}/operations/{operationId}/logs",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsGetLogsByOperationExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.environmentName,
    Parameters.operationId1,
  ],
  headerParameters: [Parameters.accept1],
  serializer,
};
const getOutputsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/environments/{environmentName}/outputs",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.EnvironmentOutputs,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsGetOutputsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listCatalogsByProjectNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedCatalog,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper:
        Mappers.EnvironmentsListCatalogsByProjectNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listEnvironmentDefinitionsByCatalogNextOperationSpec: coreClient.OperationSpec =
  {
    path: "{nextLink}",
    httpMethod: "GET",
    responses: {
      200: {
        bodyMapper: Mappers.PagedEnvironmentDefinition,
      },
      default: {
        bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
        headersMapper:
          Mappers.EnvironmentsListEnvironmentDefinitionsByCatalogNextExceptionHeaders,
      },
    },
    urlParameters: [
      Parameters.endpoint,
      Parameters.projectName,
      Parameters.catalogName,
      Parameters.nextLink,
    ],
    headerParameters: [Parameters.accept],
    serializer,
  };
const listEnvironmentDefinitionsByProjectNextOperationSpec: coreClient.OperationSpec =
  {
    path: "{nextLink}",
    httpMethod: "GET",
    responses: {
      200: {
        bodyMapper: Mappers.PagedEnvironmentDefinition,
      },
      default: {
        bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
        headersMapper:
          Mappers.EnvironmentsListEnvironmentDefinitionsByProjectNextExceptionHeaders,
      },
    },
    urlParameters: [
      Parameters.endpoint,
      Parameters.projectName,
      Parameters.nextLink,
    ],
    headerParameters: [Parameters.accept],
    serializer,
  };
const listEnvironmentTypesNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedEnvironmentType,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper:
        Mappers.EnvironmentsListEnvironmentTypesNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listEnvironmentsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedEnvironment,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsListEnvironmentsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listEnvironmentsByUserNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedEnvironment,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper:
        Mappers.EnvironmentsListEnvironmentsByUserNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listActionsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedEnvironmentAction,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsListActionsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.nextLink,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listOperationsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedEnvironmentOperation,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.EnvironmentsListOperationsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.nextLink,
    Parameters.environmentName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
